/**
 * Fetch payroll-ready spans (already split per civil day 00:00–24:00) and
 * return grouped/sorted data for easy rendering.
 *
 * @param {Object} params
 * @param {{startDate:string,endDate:string}} params.searchFilter  e.g., { startDate: "2025-10-27", endDate: "2025-10-28" }
 * @param {string[]} [params.status=[]]                            e.g., ["Done"]
 * @param {string[]} [params.serviceReportStatus=[]]               e.g., ["Completed"]
 * @param {string|null} [params.technicianId=null]                 scope to a single tech
 * @param {Function} params.getJobOrdersApi - API function getJobOrdersForPayroll(status, serviceReportStatus, startDate, endDate, technicianId)
 *
 * @returns {Promise<{spansByDate:Record<string,any[]>, flatSpans:any[], totalSpans:number}>}
 */
export async function fetchAndSplitJobOrdersForPayroll({
  searchFilter,
  status = [],
  serviceReportStatus = [],
  technicianId = null,
  getJobOrdersApi,
}) {
  if (typeof getJobOrdersApi !== "function") {
    throw new Error("getJobOrdersApi is required");
  }

  const startDate = searchFilter?.startDate;
  const endDate = searchFilter?.endDate;

  if (!startDate || !endDate) {
    return { spansByDate: {}, flatSpans: [], totalSpans: 0 };
  }

  // Call the new backend endpoint via the helper.
  // Backend already handles +1 day extension internally and returns { spans: [...] }.
  const spans = await getJobOrdersApi(
    status,
    serviceReportStatus,
    startDate,
    endDate,
    technicianId
  );

  if (!Array.isArray(spans) || spans.length === 0) {
    return { spansByDate: {}, flatSpans: [], totalSpans: 0 };
  }

  // Sort by payroll_date then by start time (start_iso)
  const flatSpans = spans
    .slice()
    .sort((a, b) => {
      if (a.payroll_date !== b.payroll_date) {
        return a.payroll_date.localeCompare(b.payroll_date);
      }
      const as = a.start_iso || "";
      const bs = b.start_iso || "";
      return as.localeCompare(bs);
    });

  // Group by payroll_date
  const spansByDate = flatSpans.reduce((acc, span) => {
    const d = span.payroll_date;
    if (!acc[d]) acc[d] = [];
    acc[d].push(span);
    return acc;
  }, {});

  return {
    spansByDate,
    flatSpans,
    totalSpans: flatSpans.length,
  };
}
